/**
 * Shared app configuration — single source of truth for API URL.
 *
 * In development (import.meta.env.DEV === true) we fall back to localhost
 * defaults so the app still works without a .env file.
 *
 * In production builds the env vars MUST be set; if they're missing the
 * fallback is still used but a prominent console error is logged so the
 * issue is caught immediately in staging/QA.
 */

const DEV = import.meta.env.DEV

export const API_BASE_URL = import.meta.env.VITE_API_URL
  || (DEV ? 'http://localhost:8000' : (console.error('[config] VITE_API_URL is not set — falling back to localhost. This MUST be fixed before release.'), 'http://localhost:8000'))

// True in local dev or when VITE_ENV=staging is set in Cloudflare Pages branch env vars.
// Set VITE_ENV=staging in Cloudflare Pages → Settings → Environment variables → Preview branch.
export const IS_STAGING = DEV || import.meta.env.VITE_ENV === 'staging'

// Stadia Maps API key — required for tile requests on deployed domains (401 without it).
// Set VITE_STADIA_API_KEY in Cloudflare Pages environment variables.
const _stadiaKey = import.meta.env.VITE_STADIA_API_KEY
if (!DEV && !_stadiaKey) console.error('[config] VITE_STADIA_API_KEY is not set — map tiles will return 401.')
export const STADIA_STYLE_URL = _stadiaKey
  ? `https://tiles.stadiamaps.com/styles/alidade_smooth.json?api_key=${_stadiaKey}`
  : 'https://tiles.stadiamaps.com/styles/alidade_smooth.json'

// Static sample report used in both Quick Predict and Quick Unlock entry points.
// Place your generated PDF at Frontend_v2/public/sample-quick-report.pdf
// or override with VITE_SAMPLE_QUICK_REPORT_URL.
export const SAMPLE_QUICK_REPORT_URL = import.meta.env.VITE_SAMPLE_QUICK_REPORT_URL || '/sample-quick-report.pdf'

// When true, fully pauses all recurring subscriptions (Home Wealth, Buyer Pass, AND Pro).
// Pricing page shows only Free Homeowner + one-time CA$9.99 report (unless PAUSE_SINGLE_REPORT=1).
// Previously-Pro features (ROI Calculator, Pro Stats) become free to drive signups & SEO:
//   • ROI Calculator — unlocked for any signed-in user (sign-in wall captures email for marketing).
//   • Pro Stats — fully public (better SEO: neighbourhood data is crawlable content).
// Existing Pro subscribers retain access via their effectiveTier; Stripe + backend stay intact.
// Flip VITE_PAUSE_SUBSCRIPTIONS back to '0' to restore the paywall instantly — no code change needed.
export const PAUSE_SUBSCRIPTIONS = import.meta.env.VITE_PAUSE_SUBSCRIPTIONS === '1'

// When true, disables the CA$9.99 single-report paywall and switches to a free lead-gen model:
// - Predictions show full results for all users (no blurred price)
// - PDF downloads are gated by email (3/day limit) instead of Stripe
// - "Connect with Agent" CTA appears on prediction and browse pages
// - Pro subscription visibility is controlled by PAUSE_SUBSCRIPTIONS (independent flag)
// Set VITE_PAUSE_SINGLE_REPORT=1 in Cloudflare Pages environment variables to activate.
export const PAUSE_SINGLE_REPORT = import.meta.env.VITE_PAUSE_SINGLE_REPORT === '1'
